/**
 * SF-4845: pick a source and a target and start a copy. Production is never a target and the
 * target list leaves out the chosen source. Before anything starts, a dialog says what happens to
 * the target and asks for its name to be typed. SF-5114: the environment this admin app runs on
 * can be the target, with a warning that this page stops working while its server is stopped.
 * Files the source does not have are always removed from the target, so there is no box for it.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Checkbox, Form, Modal, Select, Space, Typography } from 'antd';
import { TypeNameToConfirm } from '../../../companies/components/TypeNameToConfirm';
import {
  COPY_TARGETS,
  FILES_MONTHS_DEFAULT,
  SOURCES,
  confirmMatches,
  envCopyRunPath,
  envLabel,
} from '../constants';
import {
  envCopyErrorMessage,
  envCopyRunsQueryKey,
  errorStatus,
  putEnvCopyRun,
  runningRunId,
  startEnvCopyArgs,
  useEnvCopyMutation,
  type CopyStartScope,
  type EnvCopyConfig,
  type EnvCopyRun,
} from '../api/envCopyClient';
import { EditScopeNotice } from './EditScopeNotice';
import { FileCopyChoices } from './FileCopyChoices';
import { monthsLabel } from '../format';

const { Paragraph, Text } = Typography;

/** What happens to the target's admin app sign-in accounts, for the chosen option. */
const permissionsSummary = (copyPermissions: boolean) =>
  copyPermissions
    ? "The target's admin app accounts, roles and permissions are replaced with the source's."
    : 'The target keeps its own admin app accounts, roles and permissions, so people stay signed in.';

/**
 * SF-5084: what the confirm step says about files. Knowledge base documents, transcripts, uploads
 * and survey template images are always copied. SF-5114: files the source does not have are always
 * removed from those and from the chosen images and report files, month limit or not, except the
 * target's SFTP upload folders.
 */
const filesSummary = (
  source: string | undefined,
  target: string | undefined,
  copyImages: boolean,
  copyReports: boolean,
  filesMonths: number | null,
) => {
  const to = target ? envLabel(target) : 'The target';
  const from = source ? envLabel(source) : 'the source';
  const said = (what: string, copied: boolean) =>
    copied
      ? `${what} are copied after ${to} is started again.`
      : `${what} are not copied; ${to} keeps its own.`;
  const parts = [
    'Knowledge base documents, uploads, survey template images and transcripts (where both environments keep them) are always copied.',
    said('Other images', copyImages),
    said('Report files', copyReports),
  ];
  const chosen = [copyImages && 'other images', copyReports && 'report files']
    .filter(Boolean)
    .join(' and ');
  if (chosen) {
    parts.push(
      filesMonths == null
        ? 'All history is copied.'
        : `Only ${chosen} from the last ${monthsLabel(filesMonths)} are copied.`,
    );
  }
  const removed = `Files ${from} does not have are removed from the always-copied files`;
  parts.push(chosen ? `${removed} and from ${chosen}.` : `${removed}.`);
  parts.push(`${to} keeps its SFTP upload folders.`);
  return parts.join(' ');
};

interface Problem {
  message: string;
  runId?: string;
}

interface StartCopyFormProps {
  config: EnvCopyConfig;
  /** The settings are settled: not being read again and the last read worked. */
  settingsReady: boolean;
  /** Shown above the form (a failed settings refresh, with Retry). */
  notice?: React.ReactNode;
  scope: CopyStartScope;
}

export const StartCopyForm: React.FC<StartCopyFormProps> = ({
  config,
  settingsReady,
  notice,
  scope,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useEnvCopyMutation<{ run: EnvCopyRun }>();
  const [source, setSource] = useState<string | undefined>();
  const [target, setTarget] = useState<string | undefined>();
  const [copyUsage, setCopyUsage] = useState(false);
  // Off by default: the target keeps its own admin app sign-in accounts unless asked otherwise.
  const [copyPermissions, setCopyPermissions] = useState(false);
  const [copyImages, setCopyImages] = useState(false);
  const [copyReports, setCopyReports] = useState(false);
  const [filesMonths, setFilesMonths] = useState<number | null>(FILES_MONTHS_DEFAULT);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [problem, setProblem] = useState<Problem | null>(null);

  const sources = SOURCES.filter((s) => config.sources.includes(s));
  // Only stage and dev, even if the server ever listed production, and never the source.
  const targets = COPY_TARGETS.filter((t) => config.targets.includes(t) && t !== source);
  const isAvailable = (t: string) => config.available?.[t] === true;
  // SF-5114: copying onto the environment this page runs on is allowed, with a warning: its server
  // is stopped during the copy, so this page stops working until the copy has finished.
  const here = !!target && !!config.currentEnv && target === config.currentEnv;
  const hereWarning = here
    ? `You are copying onto ${envLabel(target)} from ${envLabel(target)}'s own admin app. This page stops working while the copy runs, because ${envLabel(target)}'s server is stopped. The copy carries on by itself: come back once it has finished, or follow it from another environment's admin app.`
    : null;
  const chosen = !!source && !!target && target !== source && isAvailable(target);
  // Off while the settings or the edit scope are loading, being read again or failed to load.
  const ready = chosen && settingsReady && scope.canStart;

  const anyFiles = copyImages || copyReports;

  const chooseSource = (value: string) => {
    setSource(value);
    if (value === target) setTarget(undefined);
  };

  const openConfirm = () => {
    setTyped('');
    setProblem(null);
    setConfirming(true);
  };

  const start = () => {
    if (!ready || !source || !target || !confirmMatches(typed, target)) return;
    setProblem(null);
    const body = {
      source,
      target,
      confirm: typed.trim(),
      copyUsage,
      // SF-5114: what the confirm step says. deleteExtras: true for an API from before SF-5114,
      // extrasRemoval: "all" for the current one (it refuses a page that does not send it).
      deleteExtras: true as const,
      extrasRemoval: 'all' as const,
      copyPermissions,
      copyImages,
      copyReports,
      filesMonths: anyFiles ? filesMonths : null,
    };
    mutate(startEnvCopyArgs(body), {
      onSuccess: async (answer) => {
        const run = answer?.data?.run;
        void queryClient.invalidateQueries({ queryKey: [...envCopyRunsQueryKey] });
        setConfirming(false);
        if (!run?.run_id) return;
        await putEnvCopyRun(queryClient, run);
        navigate(envCopyRunPath(run.run_id));
      },
      onError: (error) => {
        if (errorStatus(error) === 409) {
          setProblem({
            message: `A copy onto ${target} is already running`,
            runId: runningRunId(error),
          });
          return;
        }
        setProblem({ message: envCopyErrorMessage(error, 'Could not start the copy. Try again.') });
      },
    });
  };

  return (
    <Card title="Start a copy">
      {notice}
      <Form layout="vertical" style={{ maxWidth: 480 }}>
        <Form.Item label="Copy from" htmlFor="env-copy-source">
          <Select
            id="env-copy-source"
            aria-label="Copy from"
            placeholder="Choose where to copy from"
            value={source}
            onChange={chooseSource}
            options={sources.map((s) => ({ value: s, label: envLabel(s) }))}
          />
        </Form.Item>
        <Form.Item label="Copy onto" htmlFor="env-copy-target">
          <Select
            id="env-copy-target"
            aria-label="Copy onto"
            placeholder="Choose where to copy onto"
            // null, not undefined: antd keeps showing its last choice for an undefined value.
            value={target ?? null}
            onChange={setTarget}
            options={targets.map((t) => ({
              value: t,
              disabled: !isAvailable(t),
              label: isAvailable(t) ? envLabel(t) : `${envLabel(t)} (Not set up yet)`,
            }))}
          />
        </Form.Item>
        {hereWarning && (
          <Alert style={{ marginBottom: 16 }} type="warning" showIcon message={hereWarning} />
        )}
        <Form.Item>
          <Space direction="vertical">
            <Checkbox checked={copyUsage} onChange={(e) => setCopyUsage(e.target.checked)}>
              Also copy Eva usage
            </Checkbox>
            <Checkbox
              checked={copyPermissions}
              onChange={(e) => setCopyPermissions(e.target.checked)}
            >
              Copy admin app accounts and permissions from the source
            </Checkbox>
            <Text type="secondary">{permissionsSummary(copyPermissions)}</Text>
          </Space>
        </Form.Item>
        <Form.Item>
          <FileCopyChoices
            source={source}
            target={target}
            copyImages={copyImages}
            copyReports={copyReports}
            filesMonths={filesMonths}
            onCopyImages={setCopyImages}
            onCopyReports={setCopyReports}
            onFilesMonths={setFilesMonths}
          />
        </Form.Item>
        <Button type="primary" danger disabled={!ready} onClick={openConfirm}>
          Copy
        </Button>
        <div style={{ marginTop: 12 }}>
          <EditScopeNotice scope={scope} action="starting a copy" />
        </div>
      </Form>

      <Modal
        open={confirming && !!target}
        title={target ? `Replace ${envLabel(target)} with a copy of ${envLabel(source)}?` : ''}
        onCancel={() => setConfirming(false)}
        footer={[
          <Button key="cancel" onClick={() => setConfirming(false)}>
            Cancel
          </Button>,
          <Button
            key="start"
            type="primary"
            danger
            aria-busy={isLoading}
            loading={isLoading}
            disabled={!ready || !target || !confirmMatches(typed, target) || isLoading}
            onClick={start}
          >
            Start the copy
          </Button>,
        ]}
      >
        <Paragraph>
          The target database is replaced with the source snapshot. The target&apos;s sending
          domains and platform switches (including the real-email list) are kept.{' '}
          {permissionsSummary(copyPermissions)} Its Eva usage is kept unless you choose to copy
          usage.
        </Paragraph>
        <Paragraph>
          {filesSummary(source, target, copyImages, copyReports, anyFiles ? filesMonths : null)}
        </Paragraph>
        <Paragraph>
          {copyPermissions
            ? `Everyone signed in to ${envLabel(target)} is signed out, and its services are stopped while the copy runs.`
            : 'Its services are stopped while the copy runs.'}{' '}
          Campaigns and triggers on the copy are paused.
        </Paragraph>
        {hereWarning && (
          <Alert style={{ marginBottom: 16 }} type="warning" showIcon message={hereWarning} />
        )}
        {target && (
          <TypeNameToConfirm
            name={target}
            value={typed}
            onChange={setTyped}
            disabled={isLoading}
            ariaLabel="Type the target's name to confirm"
          />
        )}
        {problem && (
          <Alert
            style={{ marginTop: 16 }}
            type="error"
            showIcon
            message={problem.message}
            description={
              problem.runId ? (
                <Link to={envCopyRunPath(problem.runId)}>Open the running copy</Link>
              ) : undefined
            }
          />
        )}
        {!problem && <Text type="secondary">This cannot be undone.</Text>}
      </Modal>
    </Card>
  );
};
